import { useCallback, useContext, useEffect, useState } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import { Time } from 'lightweight-charts'
import { AppContext } from '../../../App'
import { ExtraWideTooltip } from '../../custom'
import { ChartEvents } from '../chart.events'
import { ChartPatternTargetStatus } from '../../../util/IndicatorUtils/patterns/chartPatternTypes'

export type ChartPatternTargetTooltipData = {
    type: 'chartPatternOverlay'
    time: Time
    price: number
    title: string
    status?: ChartPatternTargetStatus
    description?: string
    tooltipMode?: 'candlestick' | 'target' | 'targetLabel'
    anchorOffsetY?: number
    anchorScreenX?: number
    anchorScreenY?: number
}

type AnchorPosition = {
    x: number
    top: number
}

type Props = {
    chartId: string
}

const ChartPatternTargetTooltip = ({ chartId }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(chartId)
    const [data, setData] = useState<ChartPatternTargetTooltipData | null>(null)
    const [anchor, setAnchor] = useState<AnchorPosition | null>(null)

    const onDrawingHovered = useCallback((event: CustomEvent) => {
        const detail = event.detail as ChartPatternTargetTooltipData | null
        if (detail?.type === 'chartPatternOverlay') {
            if (detail.price != null && detail.time != null) {
                setData(detail)
            } else {
                setData(null)
            }
        } else {
            setData(null)
        }
    }, [])

    const updateAnchor = useCallback(() => {
        if (!chart?.chartApi || !data) {
            setAnchor(null)
            return
        }

        const x = chart.chartApi.timeScale().timeToCoordinate(data.time)
        if (x == null) {
            setAnchor(null)
            return
        }

        const paneId = chart.primarySeriesGroup?.pane.id
        const paneElement = paneId
            ? (chart.getPaneElementById(paneId) as HTMLElement | undefined)
            : undefined
        const paneTop = paneElement?.offsetTop ?? 0

        if (data.anchorScreenX != null && data.anchorScreenY != null) {
            setAnchor({
                x: data.anchorScreenX,
                top: paneTop + data.anchorScreenY,
            })
            return
        }

        let y: number | null = null
        const seriesApi = chart.primarySeriesGroup?.getPrimarySeriesApi()
        if (data.tooltipMode === 'candlestick' && data.anchorOffsetY != null && seriesApi) {
            const closeY = seriesApi.priceToCoordinate(data.price)
            y = closeY != null ? closeY + data.anchorOffsetY : null
        } else {
            y = seriesApi?.priceToCoordinate(data.price) ?? null
        }

        if (y == null) {
            setAnchor(null)
            return
        }

        setAnchor({
            x: x as number,
            top: paneTop + (y as number),
        })
    }, [chart, data])

    useEffect(() => {
        if (!chart) return
        chart.subscribeEvent(ChartEvents.DRAWING_HOVERED, onDrawingHovered)
        return () => chart.unsubscribeEvent(ChartEvents.DRAWING_HOVERED, onDrawingHovered)
    }, [chart, onDrawingHovered])

    useEffect(() => {
        updateAnchor()
        const chartApi = chart?.chartApi
        if (!chartApi || !data) return

        const handleRangeChange = () => updateAnchor()
        chartApi.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange)
        window.addEventListener('resize', handleRangeChange)

        return () => {
            chartApi.timeScale().unsubscribeVisibleLogicalRangeChange(handleRangeChange)
            window.removeEventListener('resize', handleRangeChange)
        }
    }, [chart, data, updateAnchor])

    if (!data || !anchor) return null

    const isCandlestick = data.tooltipMode === 'candlestick' && data.description
    const isTargetLabel = data.tooltipMode === 'targetLabel'
    const displayPrice = Number(data.price).toFixed(2)

    return (
        <ExtraWideTooltip
            open
            title={(
                <Paper
                    elevation={8}
                    sx={{
                        bgcolor: '#2A2E39',
                        px: 1.5,
                        py: 1,
                        borderRadius: '4px',
                        minWidth: isCandlestick ? 280 : 168,
                        maxWidth: isCandlestick ? 360 : undefined,
                    }}
                >
                    <Typography
                        sx={{
                            color: '#D1D4DC',
                            fontSize: 13,
                            fontWeight: 500,
                            lineHeight: 1.4,
                            mb: isCandlestick ? 0.75 : 0,
                        }}
                    >
                        {data.title}
                    </Typography>
                    {isCandlestick ? (
                        <Typography sx={{ color: '#D1D4DC', fontSize: 12, lineHeight: 1.55 }}>
                            {data.description}
                        </Typography>
                    ) : (
                        <>
                            <Typography sx={{ color: '#D1D4DC', fontSize: 13, lineHeight: 1.55 }}>
                                {`Price: ${displayPrice}`}
                            </Typography>
                            <Typography sx={{ color: '#D1D4DC', fontSize: 13, lineHeight: 1.55 }}>
                                {`Status: ${data.status}`}
                            </Typography>
                        </>
                    )}
                </Paper>
            )}
            placement="top"
            arrow
            sx={{ margin: 0, padding: 0, pointerEvents: 'none' }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    left: anchor.x,
                    top: anchor.top,
                    width: 1,
                    height: 1,
                    transform: isCandlestick || isTargetLabel ? 'translate(-50%, 0)' : 'translate(-50%, -50%)',
                }}
            />
        </ExtraWideTooltip>
    )
}

export default ChartPatternTargetTooltip
